import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Checkbox } from "../ui/checkbox";
import { 
  Users, 
  Crown, 
  DollarSign, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  Eye,
  Ban,
  Settings,
  LogOut,
  Image,
  FileText,
  Shield,
  TrendingUp,
  BarChart3,
  Flag,
  CreditCard,
  FileEdit,
  Plus,
  Upload,
  Star,
  Clock,
  MapPin,
  Heart
} from "lucide-react";

export function AdminDashboard({ onLogout }) {
  const [activeSection, setActiveSection] = useState("dashboard");
  const [createUserModal, setCreateUserModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [newUserData, setNewUserData] = useState({
    // Personal Details
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    gender: "",
    dateOfBirth: "",
    timeOfBirth: "",
    placeOfBirth: "",
    height: "",
    weight: "",
    complexion: "",
    bodyType: "",
    profileFor: "",
    religion: "Hindu",
    caste: "",
    subCaste: "",
    motherTongue: "Tamil",
    
    // Family Details
    fatherName: "",
    motherName: "",
    familyType: "",
    familyStatus: "",
    familyValues: "",
    ancestralOrigin: "",
    
    // Education & Career
    education: "",
    educationDetails: "",
    occupation: "",
    occupationDetails: "",
    income: "",
    workLocation: "",
    
    // Location
    country: "",
    state: "",
    city: "",
    residencyStatus: "",
    
    // Horoscope Details
    rasi: "",
    nakshatra: "",
    padam: "",
    gothram: "",
    dosham: "",
    manglikStatus: "",
    chevaiDosham: "",
    sarpaDosha: "",
    kalathraDosham: "",
    birthPlaceDetails: "",
    tamilMonth: "",
    
    // Partner Preferences
    partnerAgeFrom: "",
    partnerAgeTo: "",
    partnerHeightFrom: "",
    partnerHeightTo: "",
    partnerEducation: "",
    partnerOccupation: "",
    partnerIncome: "",
    partnerLocation: "",
    partnerRasi: "",
    partnerNakshatra: "",
    
    // Lifestyle
    diet: "",
    smoking: "",
    drinking: "",
    hobbies: "",
    interests: "",
    aboutMe: "",
    
    // Photos
    profilePhoto: null,
    horoscopeChart: null
  });

  const sidebarItems = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "users", label: "User Management", icon: Users },
    { id: "approvals", label: "Content Approvals", icon: CheckCircle },
    { id: "reports", label: "Reports & Moderation", icon: Flag },
    { id: "subscriptions", label: "Subscription Plans", icon: CreditCard },
    { id: "cms", label: "Content Management", icon: FileEdit }
  ];

  // Mock data
  const stats = {
    totalUsers: 12847,
    activeProfiles: 8934,
    premiumUsers: 1423,
    pendingApprovals: 47,
    reportedUsers: 12,
    monthlyRevenue: 45600,
    horoscopeProfiles: 7234,
    manglikProfiles: 1876,
    horoscopeMatches: 2341
  };

  // Horoscope data for Tamil matrimony
  const rasiOptions = [
    "Mesha (மேஷம்)", "Vrishabha (ரிஷபம்)", "Mithuna (மிதுனம்)", "Kataka (கடகம்)",
    "Simha (சிம்மம்)", "Kanya (கன்யா)", "Tula (துலாம்)", "Vrischika (விருச்சிகம்)",
    "Dhanus (தனுசு)", "Makara (மகரம்)", "Kumbha (கும்பம்)", "Meena (மீனம்)"
  ];

  const nakshatraOptions = [
    "Ashwini (அச்சுவினி)", "Bharani (பரணி)", "Krittika (கிருத்திகை)", "Rohini (ரோகிணி)",
    "Mrigashira (மிருகசீர்ஷம்)", "Ardra (ஆர்த்ரா)", "Punarvasu (புனர்வசு)", "Pushya (பூசம்)",
    "Ashlesha (ஆயில்யம்)", "Magha (மகம்)", "Purva Phalguni (பூரம்)", "Uttara Phalguni (உத்தரம்)",
    "Hasta (ஹஸ்தம்)", "Chitra (சித்திரை)", "Swati (சுவாதி)", "Vishakha (விசாகம்)",
    "Anuradha (அனுஷம்)", "Jyeshtha (கேட்டை)", "Mula (மூலம்)", "Purva Ashadha (பூராடம்)",
    "Uttara Ashadha (உத்திராடம்)", "Shravana (திருவோணம்)", "Dhanishtha (அவிட்டம்)", "Shatabhisha (சதயம்)",
    "Purva Bhadrapada (பூரட்டாதி)", "Uttara Bhadrapada (உத்திரட்டாதி)", "Revati (ரேவதி)"
  ];

  const gothramOptions = [
    "Angirasa", "Atri", "Bharadwaja", "Bhrigu", "Jamadagni", "Kashyapa", "Kasyapa",
    "Kaundinya", "Kratu", "Marichi", "Pulaha", "Pulastya", "Vashishta", "Vishwamitra",
    "Agathiyar", "Bharathwaj", "Gautama", "Harita", "Katyayana", "Parasara", "Sandilya"
  ];

  const doshamOptions = [
    "No Dosham", "Manglik", "Partial Manglik", "Chevai Dosham", "Sarpa Dosha", 
    "Kalatra Dosham", "Kaala Sarpa Dosham", "Multiple Dosham"
  ];

  // Tamil caste/community options for matrimony
  const tamilCommunities = [
    "Brahmin - Iyer", "Brahmin - Iyengar", "Brahmin - Dikshitar", "Brahmin - Gurukkal",
    "Chettiar", "Naidu", "Reddy", "Pillai", "Mudaliar", "Naicker", "Thevar", "Gounder",
    "Vanniar", "Yadava", "Vishwakarma", "Kamma", "Kallar", "Maravar", "Agamudayar",
    "Parkavakulam", "Sengunthar", "Devendra Kula Vellalar", "SC", "ST", "Other"
  ];

  // Additional location data for Tamil matrimony
  const tamilStates = [
    "Tamil Nadu", "Karnataka", "Kerala", "Andhra Pradesh", "Telangana", "Puducherry",
    "Delhi", "Mumbai", "Bangalore", "Chennai", "Hyderabad", "USA", "UK", "Canada",
    "Australia", "Singapore", "UAE", "Qatar", "Saudi Arabia", "Other"
  ];

  // Tamil months for better cultural integration
  const tamilMonths = [
    "Chithirai (சித்திரை)", "Vaikaasi (வைகாசி)", "Aani (ஆனி)", "Aadi (ஆடி)",
    "Aavani (ஆவணி)", "Purattaasi (புரட்டாசி)", "Aippasi (ஐப்பசி)", "Kaarthigai (கார்த்திகை)",
    "Margazhi (மார்கழி)", "Thai (தை)", "Maasi (மாசி)", "Panguni (பங்குனி)"
  ];

  const recentUsers = [
    { id: 1, name: "Priya Sharma", email: "priya@email.com", status: "active", joinDate: "2024-01-15" },
    { id: 2, name: "Raj Kumar", email: "raj@email.com", status: "pending", joinDate: "2024-01-14" },
    { id: 3, name: "Meera Patel", email: "meera@email.com", status: "active", joinDate: "2024-01-13" },
    { id: 4, name: "Arjun Reddy", email: "arjun@email.com", status: "suspended", joinDate: "2024-01-12" }
  ];

  const pendingApprovals = [
    { id: 1, type: "photo", user: "Priya Sharma", content: "Profile photo update", timestamp: "2 hours ago" },
    { id: 2, type: "content", user: "Raj Kumar", content: "Bio description review", timestamp: "4 hours ago" },
    { id: 3, type: "photo", user: "Meera Patel", content: "Gallery photo addition", timestamp: "6 hours ago" }
  ];

  const handleCreateUser = () => {
    // Handle user creation logic here
    console.log("Creating user with data:", newUserData);
    setCreateUserModal(false);
    setCurrentStep(1);
    // Reset form data
    setNewUserData({
      firstName: "", lastName: "", email: "", phone: "", gender: "", dateOfBirth: "",
      timeOfBirth: "", placeOfBirth: "", height: "", weight: "", complexion: "", bodyType: "",
      profileFor: "", religion: "Hindu", caste: "", subCaste: "", motherTongue: "Tamil", 
      fatherName: "", motherName: "", familyType: "", familyStatus: "", familyValues: "", 
      ancestralOrigin: "", education: "", educationDetails: "", occupation: "", occupationDetails: "", 
      income: "", workLocation: "", country: "", state: "", city: "", residencyStatus: "", 
      rasi: "", nakshatra: "", padam: "", gothram: "", dosham: "", manglikStatus: "", 
      chevaiDosham: "", sarpaDosha: "", kalathraDosham: "", birthPlaceDetails: "", tamilMonth: "",
      partnerAgeFrom: "", partnerAgeTo: "", partnerHeightFrom: "", partnerHeightTo: "", 
      partnerEducation: "", partnerOccupation: "", partnerIncome: "", partnerLocation: "", 
      partnerRasi: "", partnerNakshatra: "", diet: "", smoking: "", drinking: "", hobbies: "", 
      interests: "", aboutMe: "", profilePhoto: null, horoscopeChart: null
    });
  };

  const renderContent = () => {
    switch (activeSection) {
      case "dashboard":
        return renderDashboard();
      case "users":
        return renderUserManagement();
      case "approvals":
        return renderContentApprovals();
      case "reports":
        return renderReportsModeration();
      case "subscriptions":
        return renderSubscriptionPlans();
      case "cms":
        return renderContentManagement();
      default:
        return renderDashboard();
    }
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{stats.totalUsers.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              +12% from last month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Active Profiles</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{stats.activeProfiles.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              +8% from last month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Premium Users</CardTitle>
            <Crown className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{stats.premiumUsers.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              +23% from last month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Monthly Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">₹{stats.monthlyRevenue.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              +15% from last month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Pending Approvals</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl text-orange-600">{stats.pendingApprovals}</div>
            <p className="text-xs text-muted-foreground">
              Requires attention
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Reported Users</CardTitle>
            <XCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl text-red-600">{stats.reportedUsers}</div>
            <p className="text-xs text-muted-foreground">
              Action required
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Horoscope Profiles</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{stats.horoscopeProfiles.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Complete horoscope data
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Manglik Profiles</CardTitle>
            <Heart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl text-orange-600">{stats.manglikProfiles.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Special matching required
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Horoscope Matches</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl text-green-600">{stats.horoscopeMatches.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Compatible matches made
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Recent User Registrations</CardTitle>
            <CardDescription>Latest users who joined the platform</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {recentUsers.map((user) => (
              <div key={user.id} className="flex items-center justify-between">
                <div>
                  <p>{user.name}</p>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                </div>
                <div className="text-right">
                  <Badge variant={user.status === 'active' ? 'default' : user.status === 'pending' ? 'secondary' : 'destructive'}>
                    {user.status}
                  </Badge>
                  <p className="text-xs text-muted-foreground mt-1">{user.joinDate}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Pending Approvals</CardTitle>
            <CardDescription>Content waiting for admin approval</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {pendingApprovals.map((item) => (
              <div key={item.id} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {item.type === 'photo' ? (
                    <Image className="w-4 h-4 text-blue-500" />
                  ) : (
                    <FileText className="w-4 h-4 text-green-500" />
                  )}
                  <div>
                    <p>{item.user}</p>
                    <p className="text-sm text-muted-foreground">{item.content}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">{item.timestamp}</p>
                  <div className="flex gap-1 mt-1">
                    <Button size="sm" variant="outline" className="h-6 px-2">
                      <CheckCircle className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline" className="h-6 px-2">
                      <XCircle className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderCreateUserForm = () => {
    const stepTitles = [
      'Personal Details',
      'Family Information',
      'Education & Career',
      'Horoscope Details',
      'Partner Preferences',
      'Photos & Documents'
    ];

    return (
      <Dialog open={createUserModal} onOpenChange={setCreateUserModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0 pb-4 border-b">
            <DialogTitle>Create New User Profile</DialogTitle>
            <DialogDescription>
              Complete all sections to create a comprehensive Tamil matrimony profile
            </DialogDescription>
            
            {/* Step Progress Indicator */}
            <div className="flex items-center justify-center mt-4 px-4">
              <div className="flex items-center space-x-2">
                {[1, 2, 3, 4, 5, 6].map((step) => (
                  <div key={step} className="flex items-center">
                    <button
                      type="button"
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-colors ${
                        currentStep === step
                          ? 'bg-primary text-primary-foreground'
                          : currentStep > step
                          ? 'bg-green-500 text-white'
                          : 'bg-muted text-muted-foreground'
                      }`}
                      onClick={() => setCurrentStep(step)}
                    >
                      {currentStep > step ? '✓' : step}
                    </button>
                    {step < 6 && (
                      <div className={`w-6 h-0.5 ${currentStep > step ? 'bg-green-500' : 'bg-muted'}`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="text-center mt-2">
              <span className="text-sm">
                Step {currentStep} of 6: {stepTitles[currentStep - 1]}
              </span>
            </div>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {/* Step 1: Personal Details */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="bg-muted/30 p-4 rounded-lg">
                  <h3 className="mb-4 text-primary">Basic Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input
                        id="firstName"
                        value={newUserData.firstName}
                        onChange={(e) => setNewUserData({...newUserData, firstName: e.target.value})}
                        placeholder="Enter first name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input
                        id="lastName"
                        value={newUserData.lastName}
                        onChange={(e) => setNewUserData({...newUserData, lastName: e.target.value})}
                        placeholder="Enter last name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={newUserData.email}
                        onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                        placeholder="Enter email address"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        value={newUserData.phone}
                        onChange={(e) => setNewUserData({...newUserData, phone: e.target.value})}
                        placeholder="Enter phone number"
                      />
                    </div>
                    <div>
                      <Label htmlFor="gender">Gender *</Label>
                      <Select value={newUserData.gender} onValueChange={(value) => setNewUserData({...newUserData, gender: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="profileFor">Profile For *</Label>
                      <Select value={newUserData.profileFor} onValueChange={(value) => setNewUserData({...newUserData, profileFor: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select profile for" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="self">Self</SelectItem>
                          <SelectItem value="son">Son</SelectItem>
                          <SelectItem value="daughter">Daughter</SelectItem>
                          <SelectItem value="brother">Brother</SelectItem>
                          <SelectItem value="sister">Sister</SelectItem>
                          <SelectItem value="relative">Relative</SelectItem>
                          <SelectItem value="friend">Friend</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="bg-muted/30 p-4 rounded-lg">
                  <h3 className="mb-4 text-primary">Religious & Cultural Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="religion">Religion</Label>
                      <Select value={newUserData.religion} onValueChange={(value) => setNewUserData({...newUserData, religion: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select religion" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Hindu">Hindu</SelectItem>
                          <SelectItem value="Christian">Christian</SelectItem>
                          <SelectItem value="Muslim">Muslim</SelectItem>
                          <SelectItem value="Sikh">Sikh</SelectItem>
                          <SelectItem value="Buddhist">Buddhist</SelectItem>
                          <SelectItem value="Jain">Jain</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="motherTongue">Mother Tongue</Label>
                      <Select value={newUserData.motherTongue} onValueChange={(value) => setNewUserData({...newUserData, motherTongue: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select mother tongue" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Tamil">Tamil</SelectItem>
                          <SelectItem value="Telugu">Telugu</SelectItem>
                          <SelectItem value="Malayalam">Malayalam</SelectItem>
                          <SelectItem value="Kannada">Kannada</SelectItem>
                          <SelectItem value="Hindi">Hindi</SelectItem>
                          <SelectItem value="English">English</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="caste">Caste/Community</Label>
                      <Select value={newUserData.caste} onValueChange={(value) => setNewUserData({...newUserData, caste: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select caste/community" />
                        </SelectTrigger>
                        <SelectContent>
                          {tamilCommunities.map((community) => (
                            <SelectItem key={community} value={community}>{community}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="subCaste">Sub Caste</Label>
                      <Input
                        id="subCaste"
                        value={newUserData.subCaste}
                        onChange={(e) => setNewUserData({...newUserData, subCaste: e.target.value})}
                        placeholder="Enter sub caste (optional)"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="bg-muted/30 p-4 rounded-lg">
                  <h3 className="mb-4 text-primary">Physical Attributes</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="height">Height</Label>
                      <Select value={newUserData.height} onValueChange={(value) => setNewUserData({...newUserData, height: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select height" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="4ft6in">4'6"</SelectItem>
                          <SelectItem value="4ft8in">4'8"</SelectItem>
                          <SelectItem value="4ft10in">4'10"</SelectItem>
                          <SelectItem value="5ft0in">5'0"</SelectItem>
                          <SelectItem value="5ft2in">5'2"</SelectItem>
                          <SelectItem value="5ft4in">5'4"</SelectItem>
                          <SelectItem value="5ft6in">5'6"</SelectItem>
                          <SelectItem value="5ft8in">5'8"</SelectItem>
                          <SelectItem value="5ft10in">5'10"</SelectItem>
                          <SelectItem value="6ft0in">6'0"</SelectItem>
                          <SelectItem value="6ft2in">6'2"</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="complexion">Complexion</Label>
                      <Select value={newUserData.complexion} onValueChange={(value) => setNewUserData({...newUserData, complexion: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select complexion" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="very-fair">Very Fair</SelectItem>
                          <SelectItem value="fair">Fair</SelectItem>
                          <SelectItem value="wheatish">Wheatish</SelectItem>
                          <SelectItem value="wheatish-brown">Wheatish Brown</SelectItem>
                          <SelectItem value="dark">Dark</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="bodyType">Body Type</Label>
                      <Select value={newUserData.bodyType} onValueChange={(value) => setNewUserData({...newUserData, bodyType: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select body type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="slim">Slim</SelectItem>
                          <SelectItem value="average">Average</SelectItem>
                          <SelectItem value="athletic">Athletic</SelectItem>
                          <SelectItem value="heavy">Heavy</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="bg-muted/30 p-4 rounded-lg">
                  <h3 className="mb-4 text-primary">Birth Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="dateOfBirth">Date of Birth *</Label>
                      <Input
                        id="dateOfBirth"
                        type="date"
                        value={newUserData.dateOfBirth}
                        onChange={(e) => setNewUserData({...newUserData, dateOfBirth: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="timeOfBirth">Time of Birth</Label>
                      <Input
                        id="timeOfBirth"
                        type="time"
                        value={newUserData.timeOfBirth}
                        onChange={(e) => setNewUserData({...newUserData, timeOfBirth: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="placeOfBirth">Place of Birth</Label>
                      <Input
                        id="placeOfBirth"
                        value={newUserData.placeOfBirth}
                        onChange={(e) => setNewUserData({...newUserData, placeOfBirth: e.target.value})}
                        placeholder="Enter birth place"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Family Details */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="bg-muted/30 p-4 rounded-lg">
                  <h3 className="mb-4 text-primary">Family Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="fatherName">Father's Name</Label>
                      <Input
                        id="fatherName"
                        value={newUserData.fatherName}
                        onChange={(e) => setNewUserData({...newUserData, fatherName: e.target.value})}
                        placeholder="Enter father's name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="motherName">Mother's Name</Label>
                      <Input
                        id="motherName"
                        value={newUserData.motherName}
                        onChange={(e) => setNewUserData({...newUserData, motherName: e.target.value})}
                        placeholder="Enter mother's name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="familyType">Family Type</Label>
                      <Select value={newUserData.familyType} onValueChange={(value) => setNewUserData({...newUserData, familyType: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select family type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="nuclear">Nuclear Family</SelectItem>
                          <SelectItem value="joint">Joint Family</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="familyStatus">Family Status</Label>
                      <Select value={newUserData.familyStatus} onValueChange={(value) => setNewUserData({...newUserData, familyStatus: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select family status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="middle-class">Middle Class</SelectItem>
                          <SelectItem value="upper-middle-class">Upper Middle Class</SelectItem>
                          <SelectItem value="high-class">High Class</SelectItem>
                          <SelectItem value="rich">Rich</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="familyValues">Family Values</Label>
                      <Select value={newUserData.familyValues} onValueChange={(value) => setNewUserData({...newUserData, familyValues: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select family values" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="orthodox">Orthodox</SelectItem>
                          <SelectItem value="traditional">Traditional</SelectItem>
                          <SelectItem value="moderate">Moderate</SelectItem>
                          <SelectItem value="liberal">Liberal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="ancestralOrigin">Ancestral Origin</Label>
                      <Input
                        id="ancestralOrigin"
                        value={newUserData.ancestralOrigin}
                        onChange={(e) => setNewUserData({...newUserData, ancestralOrigin: e.target.value})}
                        placeholder="Enter ancestral origin"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Education & Career */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="bg-muted/30 p-4 rounded-lg">
                  <h3 className="mb-4 text-primary">Education & Career Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="education">Highest Education</Label>
                      <Select value={newUserData.education} onValueChange={(value) => setNewUserData({...newUserData, education: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select education" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="high-school">High School</SelectItem>
                          <SelectItem value="diploma">Diploma</SelectItem>
                          <SelectItem value="bachelors">Bachelor's Degree</SelectItem>
                          <SelectItem value="masters">Master's Degree</SelectItem>
                          <SelectItem value="phd">PhD</SelectItem>
                          <SelectItem value="professional">Professional Degree</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="educationDetails">Education Details</Label>
                      <Input
                        id="educationDetails"
                        value={newUserData.educationDetails}
                        onChange={(e) => setNewUserData({...newUserData, educationDetails: e.target.value})}
                        placeholder="e.g., B.E Computer Science"
                      />
                    </div>
                    <div>
                      <Label htmlFor="occupation">Occupation</Label>
                      <Select value={newUserData.occupation} onValueChange={(value) => setNewUserData({...newUserData, occupation: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select occupation" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="private-sector">Private Sector</SelectItem>
                          <SelectItem value="government">Government</SelectItem>
                          <SelectItem value="business">Business</SelectItem>
                          <SelectItem value="self-employed">Self Employed</SelectItem>
                          <SelectItem value="not-working">Not Working</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="occupationDetails">Occupation Details</Label>
                      <Input
                        id="occupationDetails"
                        value={newUserData.occupationDetails}
                        onChange={(e) => setNewUserData({...newUserData, occupationDetails: e.target.value})}
                        placeholder="e.g., Software Engineer"
                      />
                    </div>
                    <div>
                      <Label htmlFor="income">Annual Income</Label>
                      <Select value={newUserData.income} onValueChange={(value) => setNewUserData({...newUserData, income: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select income range" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no-income">No Income</SelectItem>
                          <SelectItem value="below-2lakh">Below ₹2 Lakh</SelectItem>
                          <SelectItem value="2-4lakh">₹2-4 Lakh</SelectItem>
                          <SelectItem value="4-6lakh">₹4-6 Lakh</SelectItem>
                          <SelectItem value="6-8lakh">₹6-8 Lakh</SelectItem>
                          <SelectItem value="8-10lakh">₹8-10 Lakh</SelectItem>
                          <SelectItem value="10-15lakh">₹10-15 Lakh</SelectItem>
                          <SelectItem value="15-20lakh">₹15-20 Lakh</SelectItem>
                          <SelectItem value="20-25lakh">₹20-25 Lakh</SelectItem>
                          <SelectItem value="25lakh-above">₹25 Lakh & Above</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="workLocation">Work Location</Label>
                      <Input
                        id="workLocation"
                        value={newUserData.workLocation}
                        onChange={(e) => setNewUserData({...newUserData, workLocation: e.target.value})}
                        placeholder="Enter work location"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-muted/30 p-4 rounded-lg">
                  <h3 className="mb-4 text-primary">Location Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="country">Country</Label>
                      <Select value={newUserData.country} onValueChange={(value) => setNewUserData({...newUserData, country: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="India">India</SelectItem>
                          <SelectItem value="USA">USA</SelectItem>
                          <SelectItem value="UK">UK</SelectItem>
                          <SelectItem value="Canada">Canada</SelectItem>
                          <SelectItem value="Australia">Australia</SelectItem>
                          <SelectItem value="Singapore">Singapore</SelectItem>
                          <SelectItem value="UAE">UAE</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="state">State</Label>
                      <Select value={newUserData.state} onValueChange={(value) => setNewUserData({...newUserData, state: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {tamilStates.map((state) => (
                            <SelectItem key={state} value={state}>{state}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={newUserData.city}
                        onChange={(e) => setNewUserData({...newUserData, city: e.target.value})}
                        placeholder="Enter city"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Horoscope Details */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="bg-muted/30 p-4 rounded-lg">
                  <h3 className="mb-4 text-primary">Basic Horoscope Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="rasi">Rasi (Moon Sign) *</Label>
                      <Select value={newUserData.rasi} onValueChange={(value) => setNewUserData({...newUserData, rasi: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Rasi" />
                        </SelectTrigger>
                        <SelectContent>
                          {rasiOptions.map((rasi) => (
                            <SelectItem key={rasi} value={rasi}>{rasi}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="nakshatra">Nakshatra (Birth Star) *</Label>
                      <Select value={newUserData.nakshatra} onValueChange={(value) => setNewUserData({...newUserData, nakshatra: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Nakshatra" />
                        </SelectTrigger>
                        <SelectContent>
                          {nakshatraOptions.map((nakshatra) => (
                            <SelectItem key={nakshatra} value={nakshatra}>{nakshatra}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="padam">Padam</Label>
                      <Select value={newUserData.padam} onValueChange={(value) => setNewUserData({...newUserData, padam: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Padam" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1</SelectItem>
                          <SelectItem value="2">2</SelectItem>
                          <SelectItem value="3">3</SelectItem>
                          <SelectItem value="4">4</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="gothram">Gothram</Label>
                      <Select value={newUserData.gothram} onValueChange={(value) => setNewUserData({...newUserData, gothram: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Gothram" />
                        </SelectTrigger>
                        <SelectContent>
                          {gothramOptions.map((gothram) => (
                            <SelectItem key={gothram} value={gothram}>{gothram}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="bg-muted/30 p-4 rounded-lg">
                  <h3 className="mb-4 text-primary">Additional Birth Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="birthPlace">Birth Place Details</Label>
                      <Input
                        id="birthPlace"
                        value={newUserData.birthPlaceDetails || ""}
                        onChange={(e) => setNewUserData({...newUserData, birthPlaceDetails: e.target.value})}
                        placeholder="e.g., Chennai, Tamil Nadu"
                      />
                    </div>
                    <div>
                      <Label htmlFor="tamilMonth">Tamil Month of Birth</Label>
                      <Select value={newUserData.tamilMonth || ""} onValueChange={(value) => setNewUserData({...newUserData, tamilMonth: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Tamil month" />
                        </SelectTrigger>
                        <SelectContent>
                          {tamilMonths.map((month) => (
                            <SelectItem key={month} value={month}>{month}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="bg-muted/30 p-4 rounded-lg">
                  <h3 className="mb-4 text-primary">Dosham Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="manglikStatus">Manglik Status</Label>
                      <Select value={newUserData.manglikStatus} onValueChange={(value) => setNewUserData({...newUserData, manglikStatus: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Manglik status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no">No</SelectItem>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="partial">Partial</SelectItem>
                          <SelectItem value="don't-know">Don't Know</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="chevaiDosham">Chevai Dosham</Label>
                      <Select value={newUserData.chevaiDosham} onValueChange={(value) => setNewUserData({...newUserData, chevaiDosham: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Chevai Dosham" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no">No</SelectItem>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="don't-know">Don't Know</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="sarpaDosha">Sarpa Dosha</Label>
                      <Select value={newUserData.sarpaDosha} onValueChange={(value) => setNewUserData({...newUserData, sarpaDosha: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Sarpa Dosha" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no">No</SelectItem>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="don't-know">Don't Know</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="kalathraDosham">Kalatra Dosham</Label>
                      <Select value={newUserData.kalathraDosham} onValueChange={(value) => setNewUserData({...newUserData, kalathraDosham: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Kalatra Dosham" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no">No</SelectItem>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="don't-know">Don't Know</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Partner Preferences */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <div className="bg-muted/30 p-4 rounded-lg">
                  <h3 className="mb-4 text-primary">Partner Preferences</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="partnerAgeFrom">Age From</Label>
                      <Input
                        id="partnerAgeFrom"
                        type="number"
                        value={newUserData.partnerAgeFrom}
                        onChange={(e) => setNewUserData({...newUserData, partnerAgeFrom: e.target.value})}
                        placeholder="Min age"
                      />
                    </div>
                    <div>
                      <Label htmlFor="partnerAgeTo">Age To</Label>
                      <Input
                        id="partnerAgeTo"
                        type="number"
                        value={newUserData.partnerAgeTo}
                        onChange={(e) => setNewUserData({...newUserData, partnerAgeTo: e.target.value})}
                        placeholder="Max age"
                      />
                    </div>
                    <div>
                      <Label htmlFor="partnerHeightFrom">Height From</Label>
                      <Input
                        id="partnerHeightFrom"
                        value={newUserData.partnerHeightFrom}
                        onChange={(e) => setNewUserData({...newUserData, partnerHeightFrom: e.target.value})}
                        placeholder="Min height"
                      />
                    </div>
                    <div>
                      <Label htmlFor="partnerHeightTo">Height To</Label>
                      <Input
                        id="partnerHeightTo"
                        value={newUserData.partnerHeightTo}
                        onChange={(e) => setNewUserData({...newUserData, partnerHeightTo: e.target.value})}
                        placeholder="Max height"
                      />
                    </div>
                    <div>
                      <Label htmlFor="partnerEducation">Partner Education</Label>
                      <Input
                        id="partnerEducation"
                        value={newUserData.partnerEducation}
                        onChange={(e) => setNewUserData({...newUserData, partnerEducation: e.target.value})}
                        placeholder="Preferred education"
                      />
                    </div>
                    <div>
                      <Label htmlFor="partnerOccupation">Partner Occupation</Label>
                      <Input
                        id="partnerOccupation"
                        value={newUserData.partnerOccupation}
                        onChange={(e) => setNewUserData({...newUserData, partnerOccupation: e.target.value})}
                        placeholder="Preferred occupation"
                      />
                    </div>
                    <div>
                      <Label htmlFor="partnerLocation">Partner Location</Label>
                      <Input
                        id="partnerLocation"
                        value={newUserData.partnerLocation}
                        onChange={(e) => setNewUserData({...newUserData, partnerLocation: e.target.value})}
                        placeholder="Preferred location"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-muted/30 p-4 rounded-lg">
                  <h3 className="mb-4 text-primary">Lifestyle Preferences</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="diet">Diet</Label>
                      <Select value={newUserData.diet} onValueChange={(value) => setNewUserData({...newUserData, diet: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select diet" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="vegetarian">Vegetarian</SelectItem>
                          <SelectItem value="non-vegetarian">Non-Vegetarian</SelectItem>
                          <SelectItem value="eggetarian">Eggetarian</SelectItem>
                          <SelectItem value="vegan">Vegan</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="smoking">Smoking</Label>
                      <Select value={newUserData.smoking} onValueChange={(value) => setNewUserData({...newUserData, smoking: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Smoking habit" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no">No</SelectItem>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="occasionally">Occasionally</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="drinking">Drinking</Label>
                      <Select value={newUserData.drinking} onValueChange={(value) => setNewUserData({...newUserData, drinking: value})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Drinking habit" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no">No</SelectItem>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="occasionally">Occasionally</SelectItem>
                          <SelectItem value="socially">Socially</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="bg-muted/30 p-4 rounded-lg">
                  <h3 className="mb-4 text-primary">About</h3>
                  <div>
                    <Label htmlFor="aboutMe">About Me</Label>
                    <Textarea
                      id="aboutMe"
                      value={newUserData.aboutMe}
                      onChange={(e) => setNewUserData({...newUserData, aboutMe: e.target.value})}
                      placeholder="Write something about yourself..."
                      rows={4}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 6: Photos */}
            {currentStep === 6 && (
              <div className="space-y-6">
                <div className="bg-muted/30 p-4 rounded-lg">
                  <h3 className="mb-4 text-primary">Photos & Documents</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="profilePhoto">Profile Photo</Label>
                      <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
                        <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">Upload profile photo</p>
                        <Button variant="outline" className="mt-2">
                          Choose File
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="horoscopeChart">Horoscope Chart</Label>
                      <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
                        <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">Upload horoscope chart</p>
                        <Button variant="outline" className="mt-2">
                          Choose File
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex-shrink-0 border-t pt-4 px-6 pb-4">
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                disabled={currentStep === 1}
                className="flex items-center gap-2"
              >
                ← Previous
              </Button>
              <div className="flex gap-2">
                {currentStep < 6 ? (
                  <Button 
                    onClick={() => setCurrentStep(Math.min(6, currentStep + 1))}
                    className="flex items-center gap-2"
                  >
                    Next →
                  </Button>
                ) : (
                  <Button 
                    onClick={handleCreateUser} 
                    className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Create Profile
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  const renderUserManagement = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="tracking-tight">User Management</h2>
          <p className="text-muted-foreground">Manage all registered users and their profiles</p>
        </div>
        <Button 
          className="flex items-center gap-2"
          onClick={() => setCreateUserModal(true)}
        >
          <Plus className="w-4 h-4" />
          Create New User
        </Button>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            {recentUsers.map((user) => (
              <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p>{user.name}</p>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                  <p className="text-xs text-muted-foreground">Joined: {user.joinDate}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={user.status === 'active' ? 'default' : user.status === 'pending' ? 'secondary' : 'destructive'}>
                    {user.status}
                  </Badge>
                  <Button size="sm" variant="outline" title="View Profile">
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" title="Suspend User">
                    <Ban className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {renderCreateUserForm()}
    </div>
  );

  const renderContentApprovals = () => (
    <Card>
      <CardHeader>
        <CardTitle>Photo & Content Approvals</CardTitle>
        <CardDescription>Review and approve user-submitted content</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {pendingApprovals.map((item) => (
            <div key={item.id} className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-3">
                {item.type === 'photo' ? (
                  <Image className="w-6 h-6 text-blue-500" />
                ) : (
                  <FileText className="w-6 h-6 text-green-500" />
                )}
                <div>
                  <p>{item.user}</p>
                  <p className="text-sm text-muted-foreground">{item.content}</p>
                  <p className="text-xs text-muted-foreground">{item.timestamp}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="bg-green-600 hover:bg-green-700">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Approve
                </Button>
                <Button size="sm" variant="destructive">
                  <XCircle className="w-4 h-4 mr-1" />
                  Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );

  const renderReportsModeration = () => (
    <Card>
      <CardHeader>
        <CardTitle>Reports & Moderation</CardTitle>
        <CardDescription>Handle user reports and moderation actions</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground">Reports and moderation tools will be implemented here.</p>
      </CardContent>
    </Card>
  );

  const renderSubscriptionPlans = () => (
    <Card>
      <CardHeader>
        <CardTitle>Subscription Plans Management</CardTitle>
        <CardDescription>Manage subscription plans and billing</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground">Subscription management tools will be implemented here.</p>
      </CardContent>
    </Card>
  );

  const renderContentManagement = () => (
    <Card>
      <CardHeader>
        <CardTitle>Content Management System</CardTitle>
        <CardDescription>Manage website content and settings</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground">CMS tools will be implemented here.</p>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Admin Header */}
      <div className="border-b bg-card">
        <div className="flex h-16 items-center px-6">
          <div className="flex items-center gap-4">
            <Shield className="h-6 w-6 text-primary" />
            <h1>Tamil Matrimony Admin</h1>
          </div>
          <div className="ml-auto flex items-center gap-4">
            <Button variant="ghost" size="sm">
              <Settings className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onLogout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 border-r bg-card h-[calc(100vh-4rem)]">
          <nav className="p-4 space-y-2">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              return (
                <Button
                  key={item.id}
                  variant={activeSection === item.id ? "default" : "ghost"}
                  className="w-full justify-start"
                  onClick={() => setActiveSection(item.id)}
                >
                  <Icon className="mr-2 h-4 w-4" />
                  {item.label}
                </Button>
              );
            })}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6 overflow-y-auto h-[calc(100vh-4rem)]">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
